/**
 * The multi-day time grid: hour gutter, all-day rails, hour-class bands,
 * gridlines, click-to-log slot stripes, and the positioned entry blocks.
 *
 * Every vertical position is a fraction of the rendered span (the visible
 * window when one is set, else the whole day), computed from the SERVER's day
 * bounds and hour marks — this file never asks what time it is locally and
 * never derives a local day. On a DST day the axis really has 23 or 25 hour
 * lines, because the marks list is the axis.
 */
import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import CalendarEntryBlock from './CalendarEntryBlock';
import { resolveWindowEdges } from './layout';
import type { DaySplit, PackedDayDual, PositionedEntry } from './layoutTypes';
import type {
  ApiCalendarEntry,
  ApiHourClassBand,
  CalendarDayView,
} from './rangeTypes';

const GRID_HEIGHT = 1200;
const RAIL_MIN_HEIGHT = 28;

interface DayRender {
  view: CalendarDayView;
  split: DaySplit;
  packed: PositionedEntry[] | PackedDayDual;
  bands: ApiHourClassBand[];
  isToday: boolean;
}

interface CalendarGridProps {
  days: DayRender[];
  entriesById: Map<string, ApiCalendarEntry>;
  compare: boolean;
  /** Visible-window edges in minutes since day open; null = full day. */
  windowStartMinute: number | null;
  windowEndMinute: number | null;
  onEntryClick: (anchor: HTMLElement, entry: ApiCalendarEntry) => void;
  onSlotClick: (dateKey: string, label: string, atMs: number) => void;
}

const pct = (fraction: number): string => `${(fraction * 100).toFixed(3)}%`;

/**
 * The same wall-clock window derivation the page hands to `packDay`
 * (`resolveWindowEdges` over the day's own marks) — one implementation, so
 * boxes, gridlines, labels, and slot instants can never disagree about where
 * "06:00" is on a DST day.
 */
const renderEdges = (
  view: CalendarDayView,
  windowStartMinute: number | null,
  windowEndMinute: number | null,
): { startMs: number; endMs: number } =>
  resolveWindowEdges(view.bounds, view.hourMarks, windowStartMinute, windowEndMinute);

const CalendarGrid: React.FC<CalendarGridProps> = ({
  days,
  entriesById,
  compare,
  windowStartMinute,
  windowEndMinute,
  onEntryClick,
  onSlotClick,
}) => {
  const theme = useTheme();
  if (days.length === 0) {
    return null;
  }

  // One gutter, labelled from the first visible day's marks. The shared
  // labels are only honest for a day whose own (fraction, label) axis agrees
  // with the gutter's; a day that deviates (a DST day in a mixed range — or
  // every OTHER day when the DST day comes first) renders its own inset
  // labels plus a clock-change indicator instead of silently misreading by
  // up to an hour against the borrowed axis (Wave 3 finding).
  const gutterDay = days[0];
  const gutterEdges = renderEdges(gutterDay.view, windowStartMinute, windowEndMinute);
  const gutterSpan = gutterEdges.endMs - gutterEdges.startMs;

  const axisSignature = (day: DayRender): string => {
    const edges = renderEdges(day.view, windowStartMinute, windowEndMinute);
    const span = edges.endMs - edges.startMs;
    if (span <= 0) {
      return '';
    }
    return day.view.hourMarks
      .filter((mark) => mark.atMs >= edges.startMs && mark.atMs < edges.endMs)
      .map((mark) => `${((mark.atMs - edges.startMs) / span).toFixed(6)}|${mark.label}`)
      .join(',');
  };
  const gutterSignature = axisSignature(gutterDay);

  const trackFor = (packed: PositionedEntry[], track?: 'plan' | 'actual') => (
    <>
      {packed.map((positioned, index) => {
        const entry = entriesById.get(positioned.sourceId);
        if (!entry) {
          return null;
        }
        return (
          <CalendarEntryBlock
            key={`${positioned.sourceId}:${positioned.isGhost ? 'ghost' : 'real'}:${positioned.segmentIndex}:${track ?? 'all'}:${index}`}
            positioned={positioned}
            entry={entry}
            onClick={onEntryClick}
          />
        );
      })}
    </>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%' }} data-testid="calendar-grid">
      {/* Hour gutter */}
      <Box sx={{ width: 56, flexShrink: 0, position: 'relative' }}>
        <Box sx={{ minHeight: RAIL_MIN_HEIGHT }} />
        <Box sx={{ position: 'relative', height: GRID_HEIGHT }}>
          {gutterDay.view.hourMarks.map((mark, index) => {
            const fraction = (mark.atMs - gutterEdges.startMs) / gutterSpan;
            if (fraction < 0 || fraction >= 1) {
              return null;
            }
            return (
              <Typography
                key={`${mark.label}-${index}`}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  top: pct(fraction),
                  right: 6,
                  transform: 'translateY(-50%)',
                }}
              >
                {mark.label}
              </Typography>
            );
          })}
        </Box>
      </Box>

      {/* Day columns */}
      {days.map((day) => {
        const edges = renderEdges(day.view, windowStartMinute, windowEndMinute);
        const span = edges.endMs - edges.startMs;
        const marks = day.view.hourMarks.filter(
          (mark) => mark.atMs >= edges.startMs && mark.atMs < edges.endMs,
        );
        const deviates = axisSignature(day) !== gutterSignature;
        return (
          <Box
            key={day.view.bounds.dateKey}
            data-testid={`day-column-${day.view.bounds.dateKey}`}
            data-day-column={day.view.bounds.dateKey}
            data-today={day.isToday ? 'true' : undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              borderLeft: `1px solid ${theme.palette.divider}`,
              backgroundColor: day.isToday
                ? alpha(theme.palette.primary.main, 0.04)
                : undefined,
            }}
          >
            {/* All-day rail */}
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              data-testid={`all-day-rail-${day.view.bounds.dateKey}`}
              sx={{
                minHeight: RAIL_MIN_HEIGHT,
                px: 0.5,
                py: 0.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              {deviates && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  data-testid={`dst-shift-${day.view.bounds.dateKey}`}
                  label="clock change"
                  title="This day's clock shifts, so the shared hour gutter does not apply — read times from the labels inside this column."
                />
              )}
              {day.split.allDayRail.map((segment) => {
                const entry = entriesById.get(segment.sourceId);
                if (!entry) {
                  return null;
                }
                return (
                  <Chip
                    key={segment.sourceId}
                    size="small"
                    variant="outlined"
                    data-entry-kind={entry.kind}
                    label={`${entry.title || 'untitled'} · all-day`}
                    onClick={(event) =>
                      onEntryClick(event.currentTarget as HTMLElement, entry)
                    }
                  />
                );
              })}
            </Stack>

            {/* Time area */}
            <Box sx={{ position: 'relative', height: GRID_HEIGHT }}>
              {/* Hour-class background bands */}
              {day.bands.map((band, index) => {
                const startMs = Date.parse(band.start_at);
                const endMs = Date.parse(band.end_at);
                const top = Math.max(0, (startMs - edges.startMs) / span);
                const bottom = Math.min(1, (endMs - edges.startMs) / span);
                if (bottom <= 0 || top >= 1 || bottom <= top) {
                  return null;
                }
                return (
                  <Box
                    key={`${band.hour_class_id}-${index}`}
                    title={band.name}
                    data-hour-class-band={band.name}
                    sx={{
                      position: 'absolute',
                      top: pct(top),
                      height: pct(bottom - top),
                      left: 0,
                      right: 0,
                      backgroundColor: alpha(theme.palette.info.main, 0.07),
                      zIndex: 0,
                    }}
                  />
                );
              })}

              {/* Gridlines + click-to-log slot stripes, one per real hour */}
              {marks.map((mark, index) => {
                const top = (mark.atMs - edges.startMs) / span;
                const nextMs =
                  index + 1 < marks.length ? marks[index + 1].atMs : edges.endMs;
                const height = (nextMs - mark.atMs) / span;
                return (
                  <Box
                    key={`${mark.label}-${index}`}
                    component="button"
                    type="button"
                    aria-label={`Log time here: ${day.view.bounds.dateKey} ${mark.label}`}
                    onClick={() =>
                      onSlotClick(day.view.bounds.dateKey, mark.label, mark.atMs)
                    }
                    sx={{
                      position: 'absolute',
                      top: pct(top),
                      height: pct(height),
                      left: 0,
                      right: 0,
                      width: '100%',
                      border: 'none',
                      borderTop: `1px solid ${theme.palette.divider}`,
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      zIndex: 1,
                      padding: 0,
                    }}
                  />
                );
              })}

              {/* Own-axis inset labels for a day the shared gutter misreads */}
              {deviates && (
                <Box
                  data-testid={`inset-hour-labels-${day.view.bounds.dateKey}`}
                  sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3 }}
                >
                  {marks.map((mark, index) => (
                    <Typography
                      key={`inset-${mark.label}-${index}`}
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        position: 'absolute',
                        top: pct((mark.atMs - edges.startMs) / span),
                        left: 2,
                        transform: 'translateY(-50%)',
                        fontSize: '0.6rem',
                        opacity: 0.8,
                      }}
                    >
                      {mark.label}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Entries */}
              {compare ? (
                <>
                  <Box
                    data-testid={`plan-track-${day.view.bounds.dateKey}`}
                    sx={{ position: 'absolute', inset: 0, right: '50%', zIndex: 2 }}
                  >
                    {trackFor((day.packed as PackedDayDual).plan, 'plan')}
                  </Box>
                  <Box
                    data-testid={`actual-track-${day.view.bounds.dateKey}`}
                    sx={{ position: 'absolute', inset: 0, left: '50%', zIndex: 2 }}
                  >
                    {trackFor((day.packed as PackedDayDual).actual, 'actual')}
                  </Box>
                </>
              ) : (
                trackFor(day.packed as PositionedEntry[])
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CalendarGrid;
export type { DayRender };
